import { type ComponentProps, useEffect, useMemo, useState } from 'react'

import { ChevronFlip, Collapse, type Link } from '@subaio/components'
import { isNonNullable } from '@subaio/utils'

import { isIdActive } from '../../isIdActive'
import type { Heading } from '..'
import { Button } from './Button'
import { Container } from './Container'
import { Entry } from './Entry'
import { OrderedList } from './OrderedList'

type Props = {
	title: string
	id: string
	paths: Array<Heading & { method: string }>
	fragment: string | undefined
} & ComponentProps<typeof Link>

export const ExpandLink = ({ title, children, id, paths, name, params, fragment, ...rest }: Props) => {
	const isActive = isIdActive(fragment, id)
	const [isActiveLookup, setisActiveLookup] = useState(() =>
		Object.fromEntries(paths.map(({ id }) => [id, id === fragment] as const)),
	)

	let hasHighlighted = false
	const isSubsectionActive = (id: string) => {
		if (hasHighlighted || !isActiveLookup[id]) return false

		hasHighlighted = true

		return true
	}

	const observer = useMemo(
		() =>
			new IntersectionObserver((entries) => {
				const update = Object.fromEntries(entries.map(({ target, isIntersecting }) => [target.id, isIntersecting]))
				setisActiveLookup((current) => ({ ...current, ...update }))
			}),
		[],
	)

	useEffect(() => {
		if (!isActive) return

		const sections = paths.map(({ id }) => document.getElementById(id)).filter(isNonNullable)

		for (const section of sections) {
			observer.observe(section)
		}

		return () => {
			for (const section of sections) {
				observer.unobserve(section)
			}
		}
	}, [isActive, observer, paths])

	return (
		<Container>
			<Button {...rest} name={name} params={params} fragment={id} isActive={isActive} replace>
				{title}
				<ChevronFlip isActive={isActive} style={{ marginBottom: '5px' }} bold />
			</Button>
			<Collapse isOpen={isActive} fill>
				<OrderedList>
					{paths.map(({ title, id }) => (
						<Entry key={id} title={title} name={name} params={params} id={id} isActive={isSubsectionActive(id)} />
					))}
				</OrderedList>
			</Collapse>
		</Container>
	)
}
